import { createFileRoute, Link } from '@tanstack/react-router';
import { useT } from '@/i18n/locale';
import { StatusBadge } from '@/components/StatusBadge';
import { SeatMeter } from '@/components/SeatMeter';
import { useInstances } from '@/api/hooks';
import { trialDaysLeft } from '@/lib/instances';

export const Route = createFileRoute('/_authenticated/instances/')({
  component: InstancesListPage,
});

function InstancesListPage() {
  const t = useT();
  const { data: instances, isPending, isError, refetch } = useInstances();

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">{t('instances.title')}</h1>
          <p className="mt-1 text-sm text-slate-500">{t('instances.subtitle')}</p>
        </div>
        <Link
          to="/instances/new"
          className="inline-flex items-center rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
        >
          {t('instances.add')}
        </Link>
      </div>

      <div className="mt-4 rounded-lg bg-brand-50 p-3 text-sm text-brand-800 ring-1 ring-inset ring-brand-200">
        {t('instances.seatModelBanner')}
      </div>

      {isPending ? (
        <p className="mt-10 text-center text-sm text-slate-500">{t('generic.loading')}</p>
      ) : isError ? (
        <div className="mt-10 flex flex-col items-center gap-3 text-center">
          <p className="text-sm text-red-600">{t('instances.loadError')}</p>
          <button
            type="button"
            onClick={() => void refetch()}
            className="rounded-lg border border-slate-200 px-3.5 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            {t('generic.retry')}
          </button>
        </div>
      ) : instances.length === 0 ? (
        <p className="mt-10 text-center text-sm text-slate-500">{t('instances.empty')}</p>
      ) : (
        <ul className="mt-6 grid gap-4 sm:grid-cols-2">
          {instances.map((instance) => (
            <li key={instance.id} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-start justify-between gap-2">
                <Link
                  to="/instances/$id"
                  params={{ id: instance.id }}
                  className="truncate font-medium text-slate-900 hover:text-brand-700"
                  title={instance.urlLabel ?? instance.identValue}
                >
                  {instance.urlLabel ?? instance.identValue}
                </Link>
                <StatusBadge status={instance.status} trialDaysLeft={trialDaysLeft(instance.trialEndsAt)} />
              </div>

              {!instance.pinnedUuid && (
                <p className="mt-1 text-xs text-slate-400">{t('instances.card.unpinned')}</p>
              )}

              <div className="mt-4">
                <SeatMeter
                  purchased={instance.seatsPurchased}
                  active={instance.seatsActive}
                  recommended={instance.seatsRecommended}
                  isTrial={instance.status === 'trial'}
                />
              </div>

              <div className="mt-4 flex gap-2">
                {/* The actual checkout / portal-session actions live on the detail page (BILLING_PLAN.md
                    §13) — this is only a navigational shortcut into the right section there. */}
                <Link
                  to="/instances/$id"
                  params={{ id: instance.id }}
                  className="inline-flex items-center justify-center rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-700"
                >
                  {instance.hasSubscription ? t('instances.card.manageSubscription') : t('instances.card.buySeats')}
                </Link>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
